// AuthContext.js — front-end sign-in gate + per-user page access.
//
// Sign-in is validated by the backend against the `permissions_ettifaq` ClickHouse
// table (see backend/permissions.py), which also stores each user's allowed pages.
// The admin (jack.lee) manages accounts + access from the Permissions page.
//
// NOTE: this remains a convenience gate, not hardened security — the backend
// endpoints are open like the rest of the app. If ClickHouse is unreachable at
// sign-in, we fall back to the built-in account list (full access) so nobody is
// locked out during an outage.
import React, { createContext, useContext, useState, useCallback } from 'react';
import { PAGE_VIEWS, canonicalGrantId, canonicalPageId } from './pageRoutes';

const AUTH_KEY = 'ettifaq_auth_user';
const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8010';

// Built-in fallback list — only used when the backend can't be reached.
const VALID_LOGINS = {
  'jack.lee': 'jkflee-ettifaq',
  'ralph.napoli': 'rnapoli-ettifaq',
  'george.apostolidis': 'gapostolidis-ettifaq',
  'arthur.paps': 'apapas-ettifaq',
  'cody.williamson': 'cwilliamson-ettifaq',
  'sabri.hadhri': 'shadhri-ettifaq',
  'billel.arab': 'barab-ettifaq',
};
const ADMINS = new Set(['jack.lee']);

const titleCase = (u) =>
  u.replace(/\./g, ' ').split(/\s+/).filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') || u;

// Does `user` have access to a given page id? Home is always open; the Permissions
// page is admin-only; admins (or an 'ALL' grant) can open everything.
export function userCanAccess(user, pageId) {
  if (!user) return false;
  if (pageId === '/') return true;
  if (pageId === '/permissions') return !!user.isAdmin;
  if (user.isAdmin) return true;
  const ap = user.allowedPages;
  if (ap === 'ALL' || ap == null) return true;
  // A page made of views (pageRoutes.js PAGE_VIEWS) opens for anyone who can open one of them.
  if (PAGE_VIEWS[pageId]) return Object.values(PAGE_VIEWS[pageId]).some((v) => userCanAccess(user, v));
  // Compared on the canonical id, so a grant saved before a page was renamed still opens it.
  // The stored string is whatever it was when an admin last pressed Save, and that may predate
  // any number of renames — see pageRoutes.js.
  const allowed = String(ap).split(',').map((s) => s.trim()).filter(Boolean).map(canonicalGrantId);
  return allowed.includes(canonicalPageId(pageId));
}

// Does `user` hold a granular feature capability (see capabilities.js)? Admins hold
// every capability; otherwise it must be listed in their `capabilities` grant.
export function userHasCapability(user, key) {
  if (!user) return false;
  if (user.isAdmin) return true;
  const caps = user.capabilities;
  if (caps === 'ALL') return true;
  if (caps == null) return false;
  return String(caps).split(',').map((s) => s.trim()).filter(Boolean).includes(key);
}

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem(AUTH_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  const persist = useCallback((usr) => {
    localStorage.setItem(AUTH_KEY, JSON.stringify(usr));
    setUser(usr);
  }, []);

  // Returns true on success, false on bad credentials. Async (hits the backend).
  const login = useCallback(async (username, password) => {
    const u = (username || '').trim().toLowerCase();
    if (!u) return false;

    // Primary path: validate against the backend / ClickHouse.
    try {
      const res = await fetch(`${API_BASE}/permissions/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: u, password }),
      });
      if (res.ok) {
        const d = await res.json();
        if (d && d.ok && d.user) {
          persist({
            username: d.user.username,
            displayName: d.user.displayName || titleCase(d.user.username),
            isAdmin: !!d.user.isAdmin,
            allowedPages: d.user.allowedPages != null ? d.user.allowedPages : 'ALL',
            capabilities: d.user.capabilities != null ? d.user.capabilities : '',
            competitions: d.user.competitions != null ? d.user.competitions : 'ALL',
          });
          return true;
        }
        return false; // backend reachable but rejected the credentials
      }
      // non-OK (e.g. 503 ClickHouse waking) → fall through to the offline fallback
    } catch {
      // network error → fall through to the offline fallback
    }

    // Fallback: built-in list (full page access) so an outage doesn't lock people
    // out. Granular capabilities aren't known offline, so admins get all and others
    // get none (the safe default) until the backend is reachable again. Competition
    // access goes the other way — it is a licence-shaped restriction, not a feature
    // grant, and blanking every provider picker is a worse outage than a wide one.
    if (Object.prototype.hasOwnProperty.call(VALID_LOGINS, u) && VALID_LOGINS[u] === password) {
      persist({ username: u, displayName: titleCase(u), isAdmin: ADMINS.has(u), allowedPages: 'ALL', capabilities: ADMINS.has(u) ? 'ALL' : '', competitions: 'ALL' });
      return true;
    }
    return false;
  }, [persist]);

  const logout = useCallback(() => {
    localStorage.removeItem(AUTH_KEY);
    setUser(null);
  }, []);

  const can = useCallback((pageId) => userCanAccess(user, pageId), [user]);
  const hasCapability = useCallback((key) => userHasCapability(user, key), [user]);

  return (
    <AuthContext.Provider
      value={{ isAuthenticated: !!user, user, isAdmin: !!(user && user.isAdmin), login, logout, can, hasCapability }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export default AuthContext;
